/**
 * @fileoverview The card's own copy of a plant's photographs, and which one is showing.
 *
 * A card keeps its own list rather than reading the garden query directly,
 * because uploading, editing, deleting and moving a picture all have to be
 * visible on the card the instant they happen — long before anything refetches
 * the garden. Keeping that list in step is a job with four cases and it was
 * written out among the JSX.
 *
 * Where: the plant card (PlantCard); useLocationPhotos is its twin for beds.
 * API: none; it preloads the picture on screen and writes the card's list
 * into the plants cache (applyPhotosToPlantsCache).
 *
 * @module usePlantPhotos
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plant } from '@/shared/types/plant';
import { PlantPhoto } from '@/shared/types/photo';
import { photoUrl } from './plantPhotoUrl';
import { applyPhotosToPlantsCache } from './plantNotesCache';

/** What a card needs to show and change its photographs. */
export interface PlantPhotos {
  /** The card's own list, newest first. */
  photos: PlantPhoto[];
  /** Index into photos of the picture on screen. */
  selected: number;
  /** Shows the picture at an index. */
  select: (index: number) => void;
  /** The photo on screen, or undefined when the plant has none. */
  current: PlantPhoto | undefined;
  /** True while the picture on screen is still being fetched. */
  isLoading: boolean;
  /** Takes an uploaded or edited photo into the list, and shows it. */
  received: (photo: PlantPhoto) => void;
  /** Drops a photo that has been deleted, or has moved to another plant. */
  removed: (photoId: number) => void;
  /** The words about the picture on screen, if it has any. */
  note: string | null;
}

/**
 * A card's photographs: its own list kept in step with the garden query,
 * the one on screen, and its words.
 *
 * @param plant - The plant as the garden query has it. Its photos seed the
 *   list, and replace it whenever the garden is refetched.
 * @returns The list, the one on screen, and the ways to change them.
 */
export function usePlantPhotos(plant: Plant): PlantPhotos {
  const queryClient = useQueryClient();
  const [photos, setPhotos] = useState<PlantPhoto[]>((plant.photos as PlantPhoto[]) || []);
  const [selected, setSelected] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * The list as it stands, readable from inside a state updater.
   *
   * Working out which photo to keep showing means comparing the incoming list
   * against the current one, and a setState updater cannot see other state.
   */
  const photosRef = useRef(photos);
  photosRef.current = photos;

  /**
   * The photograph just added or edited, held until the garden has put it in
   * its place.
   *
   * A new picture goes to the front at once, but the server files it by the
   * day it was taken: one from between September and October lands behind
   * November when the plant comes back from the server. Whatever the card was
   * showing a moment before, it is this picture the gardener wants to see, so
   * the card goes wherever it lands.
   */
  const pinnedId = useRef<number | null>(null);

  /*
   * The garden refetched, or the plant changed underneath this card.
   *
   * Keep looking at the same photograph. React Query hands back a new array
   * on every refetch, so this ran constantly, and it used to reset the
   * carousel to the first picture each time — saving a note on one plant
   * snapped every card in the garden back to its newest photo, including the
   * one the gardener was in the middle of looking at. The selection follows
   * the photo's id, and only falls back to the front when that photo is
   * genuinely gone.
   */
  useEffect(() => {
    if (!plant.photos) return;
    const next = plant.photos as PlantPhoto[];

    setSelected((current) => {
      const pinned = pinnedId.current === null
        ? -1
        : next.findIndex((photo) => photo.photo_id === pinnedId.current);
      if (pinned >= 0) {
        // Let go once the server's own list has it; the card's own copy,
        // published a moment ago, is not yet where it will settle.
        if (next !== photosRef.current) pinnedId.current = null;
        return pinned;
      }
      const showing = photosRef.current[current];
      if (!showing) return 0;
      const stillThere = next.findIndex(
        (photo) => photo.photo_id === showing.photo_id
      );
      return stillThere >= 0 ? stillThere : 0;
    });
    setPhotos(next);
  }, [plant.photos]);

  /*
   * Fetch the picture before showing it.
   *
   * Without this the <img> paints its own loading: a white box that becomes a
   * photograph, one row at a time, which looks like a fault. The spinner is
   * held until the browser has the whole thing.
   */
  useEffect(() => {
    if (photos.length === 0) return;
    const image = new Image();
    image.src = photoUrl(photos[selected], 'card');
    image.onload = () => setIsLoading(false);
    image.onerror = () => setIsLoading(false);
    setIsLoading(true);
    // A picture swiped past before it arrived must not end the next one's spinner.
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [selected, photos]);

  /**
   * Tells the garden what this card now knows.
   *
   * The card's list and the query cache have to say the same thing. They did
   * not: the card kept an uploaded photo locally while the cache still held
   * the list without it, so anything that changed the cache — saving the
   * photo's note, most obviously — re-seeded the card from the older list and
   * the new picture vanished a moment after it appeared.
   *
   * @param next - The card's list as it now stands.
   */
  const publish = useCallback((next: PlantPhoto[]) => {
    applyPhotosToPlantsCache(queryClient, plant.plant_obj_id, next);
  }, [queryClient, plant.plant_obj_id]);

  /** Takes an uploaded or edited photo into the list, shows it, and tells the garden. */
  const received = useCallback((photo: PlantPhoto) => {
    /*
     * Computed from the ref rather than inside a setState updater.
     *
     * Publishing to the query cache is a side effect, and React is free to run
     * an updater more than once; a side effect in one is a side effect that
     * may happen twice.
     */
    const current = photosRef.current;
    const at = current.findIndex(
      (existing) => existing.photo_id === photo.photo_id
    );

    let next: PlantPhoto[];
    if (at < 0) {
      // New pictures go to the front, which is where the newest one belongs.
      next = [photo, ...current];
      setSelected(0);
    } else {
      next = [...current];
      next[at] = {
        ...current[at],
        ...photo,
        // Named individually because an edit's reply carries only what it
        // changed, and spreading undefined over a good URL blanks the picture.
        photo_id: photo.photo_id,
        signed_photo_url: photo.signed_photo_url,
        cardUrl: photo.cardUrl,
        thumbUrl: photo.thumbUrl,
        // The photo's own stage if the reply names one, else the one it had:
        // a photo's stage is its own, never borrowed from the plant.
        stage: photo.stage || current[at].stage,
        date_taken: photo.date_taken,
      };
      setSelected(at);
    }

    pinnedId.current = photo.photo_id;
    photosRef.current = next;
    setPhotos(next);
    publish(next);
    setIsLoading(true);
  }, [publish]);

  /** Drops a deleted or moved photo, keeps the selection in range, and tells the garden. */
  const removed = useCallback((photoId: number) => {
    const next = photosRef.current.filter(
      (p) => p.photo_id !== photoId
    );
    // Showing the fourth of three pictures shows nothing at all.
    setSelected((at) => (at >= next.length ? Math.max(0, next.length - 1) : at));
    photosRef.current = next;
    setPhotos(next);
    publish(next);
  }, [publish]);

  /**
   * The words belonging to the picture on screen.
   *
   * A photo note and a card note are different things. This one is about the
   * picture, so it is shown on the picture; the journal below is for notes
   * about the plant, and the two never stand in for each other.
   *
   * Matched by id against the notes already in the payload, so it costs no
   * request.
   */
  const note = useMemo(() => {
    const noteId = photos[selected]?.note_id;
    if (!noteId) return null;
    return plant.notes?.find((n) => n.note_id === noteId)?.content ?? null;
  }, [photos, selected, plant.notes]);

  return {
    photos,
    selected,
    select: setSelected,
    current: photos[selected],
    isLoading,
    received,
    removed,
    note,
  };
}
